import { CheckCircleIcon, InfoIcon, XCircleIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'
import { IglooCustomPin } from '@/models'
import { formatDateOnly } from '@/utils/date'

import * as IS from './igloo-tab.styled'

interface IglooCustomPinBlockProps {
  pin: IglooCustomPin
  canEdit: boolean
  residentName: string
  onRevoke: () => Promise<void>
  onResend: () => void
  isRevoking: boolean
  isResending: boolean
}

export const IglooCustomPinBlock = ({
  pin,
  canEdit,
  residentName,
  onRevoke,
  onResend,
  isRevoking,
  isResending,
}: IglooCustomPinBlockProps) => {
  const { t } = useTranslation(['clients'])

  const handleRevokeClick = () =>
    popupFactory.open(ConfirmationPopup, {
      cancelText: t('clients:keylessAccess.igloo.customPin.revoke.cancelText'),
      children: t('clients:keylessAccess.igloo.customPin.revoke.confirmBody', {
        residentName:
          residentName || t('clients:keylessAccess.igloo.customPin.revoke.residentFallback'),
      }),
      confirmText: t('clients:keylessAccess.igloo.customPin.revoke.confirmText'),
      onConfirm: onRevoke,
      title: t('clients:keylessAccess.igloo.customPin.revoke.confirmTitle'),
      variant: 'danger',
    })

  // Staff never create a custom PIN — the resident does, from their app. With no record there is
  // nothing to show and no action to offer.
  if (pin.status === 'none') return null

  if (pin.status === 'pending') {
    return (
      <IS.PinBlock>
        <IS.IssuedInfo>
          <Typography size="text-medium" color="black500" as="span">
            {t('clients:keylessAccess.igloo.customPin.pendingActivation')}
          </Typography>
          <Tooltip triggerIcon={InfoIcon}>
            {t('clients:keylessAccess.igloo.customPin.tooltip')}
          </Tooltip>
        </IS.IssuedInfo>
      </IS.PinBlock>
    )
  }

  if (pin.status === 'revoked') {
    return (
      <IS.PinBlock>
        <IS.IssuedInfo>
          <Typography size="text-medium" color="black800" as="span">
            {t('clients:keylessAccess.igloo.customPin.revoked')}
          </Typography>
          <Icon icon={CheckCircleIcon} size="1.8rem" color="green600" />
          <Typography size="text-medium" color="black500" as="span">
            |
          </Typography>
          <Typography size="text-medium" color="black800" as="span">
            {t('clients:keylessAccess.igloo.customPin.unitLockSyncRequired')}
          </Typography>
          <Tooltip triggerIcon={InfoIcon}>
            {t('clients:keylessAccess.igloo.customPin.syncTooltip')}
          </Tooltip>
        </IS.IssuedInfo>
      </IS.PinBlock>
    )
  }

  // status === 'active'
  return (
    <IS.PinBlock>
      <Flex align="center" justify="space-between" gap={12} wrap="wrap">
        <Typography size="text-medium" color="black800" as="span">
          {pin.activatedAt
            ? t('clients:keylessAccess.igloo.customPin.issuedOn', {
                date: formatDateOnly(pin.activatedAt),
              })
            : t('clients:keylessAccess.igloo.customPin.issued')}
        </Typography>
        <Flex align="center" gap={8} wrap="wrap">
          {canEdit && (pin.emailStatus === 'failed' || pin.emailStatus === 'pending') && (
            <Button use="primary" size="small" loading={isResending} onClick={() => onResend()}>
              {t('clients:keylessAccess.igloo.customPin.resendEmail')}
            </Button>
          )}
          {canEdit && (
            <Button use="primary" size="small" loading={isRevoking} onClick={handleRevokeClick}>
              {t('clients:keylessAccess.igloo.customPin.revoke.action')}
            </Button>
          )}
          <Tooltip triggerIcon={InfoIcon}>
            {t('clients:keylessAccess.igloo.customPin.tooltip')}
          </Tooltip>
        </Flex>
      </Flex>
      <Flex align="center" gap={8}>
        <Typography size="text-medium" color="primary500" as="span">
          {t('clients:keylessAccess.igloo.customPin.emailStatus.label')}
        </Typography>
        {pin.emailStatus === 'failed' ? (
          <>
            <Icon icon={XCircleIcon} size="1.8rem" color="red600" />
            <Typography size="text-medium" color="red600" weight="medium" as="span">
              {t('clients:keylessAccess.igloo.customPin.emailStatus.failed')}
            </Typography>
          </>
        ) : pin.emailStatus === 'sent' ? (
          <>
            <Icon icon={CheckCircleIcon} size="1.8rem" color="green600" />
            <Typography size="text-medium" color="green600" weight="medium" as="span">
              {t('clients:keylessAccess.igloo.customPin.emailStatus.sent')}
            </Typography>
          </>
        ) : (
          <Typography size="text-medium" color="black500" weight="medium" as="span">
            {t('clients:keylessAccess.igloo.customPin.emailStatus.pending')}
          </Typography>
        )}
      </Flex>
    </IS.PinBlock>
  )
}
